import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import type { PermissionKey } from '@/types/permissions';
import { useHasPermission, type PermissionMode } from './permissions';
import { paths } from '@/routes/paths';

interface PermissionGuardProps {
  permission: PermissionKey | PermissionKey[];
  mode?: PermissionMode;
  children: ReactNode;
  /** When set, render inline instead of redirecting to the 403 page. */
  inlineFallback?: ReactNode;
}

/**
 * Route-level permission gate. Redirects to /403 (or renders an inline fallback)
 * when the current user lacks the required permission. The backend still enforces
 * the real check on every request (spec §36).
 */
export function PermissionGuard({
  permission,
  mode = 'all',
  children,
  inlineFallback,
}: PermissionGuardProps) {
  const allowed = useHasPermission(permission, mode);
  if (allowed) return <>{children}</>;
  if (inlineFallback !== undefined) return <>{inlineFallback}</>;
  return <Navigate to={paths.forbidden} replace />;
}
